/**
 * Таблица структуры: культура · полей · площадь · доля от пашни · факт
 * прошлого сезона · изменение · урожайность · валовка · техкарты.
 *
 * Форма намеренно повторяет живой экран «Структура посевных площадей» на
 * дашборде — агроном видит в плане то же, что привык видеть по факту.
 */
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  formatDelta,
  formatHa,
  formatShare,
  formatTons,
  formatYield,
  shareBarWidth,
  techCardsLabel,
  type CropStructure,
  type CropStructureRow,
} from './sppApi';

function ShareBar({ row }: { row: CropStructureRow }) {
  if (row.share_pct === null) {
    return <span className="text-muted-foreground">выведена из структуры</span>;
  }
  return (
    <span className="flex items-center gap-2">
      <span className="h-2 w-24 overflow-hidden rounded-full bg-muted">
        <span
          className={cn(
            'block h-full rounded-full',
            row.kind === 'unassigned' ? 'bg-accent-brand' : 'bg-primary',
          )}
          style={{ width: shareBarWidth(row.share_pct) }}
        />
      </span>
      <span className="tabular-nums">{formatShare(row.share_pct)}</span>
    </span>
  );
}

export function StructureTable({ data }: { data: CropStructure }) {
  const prevYear = data.prev_season_year;
  // Нормативы техкарт рисуем только тем, кому техкарты открыты правом: стена
  // прочерков вместо цифр — это не «честно пусто», а мусор. Причину объясняет
  // текст под таблицей (`economicsHiddenNote`).
  const economics = data.economics_visible;
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Культура</TableHead>
          <TableHead className="text-right">Полей</TableHead>
          <TableHead className="text-right">Площадь {data.season_year}, га</TableHead>
          <TableHead>Доля от пашни</TableHead>
          <TableHead className="text-right">
            {prevYear ? `Факт ${prevYear}, га` : 'Факт прошлого сезона'}
          </TableHead>
          <TableHead className="text-right">Изменение</TableHead>
          {economics && (
            <>
              <TableHead className="text-right">Урожайность, ц/га</TableHead>
              <TableHead className="text-right">Валовка, т</TableHead>
              <TableHead>Техкарты</TableHead>
            </>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.items.map((row) => (
          <TableRow key={row.key}>
            <TableCell
              className={cn(
                'font-medium',
                row.kind === 'dropped' && 'text-muted-foreground',
                row.kind === 'unassigned' && 'text-accent-brand',
              )}
            >
              {row.name}
              {row.kind === 'dropped' && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  — в {data.season_year} не сеем
                </span>
              )}
              {row.kind === 'fallow' && (
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  — поле отдыхает
                </span>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {row.fields_count === null ? '—' : row.fields_count}
            </TableCell>
            <TableCell className="text-right tabular-nums">{formatHa(row.area_ha)}</TableCell>
            <TableCell className="text-sm">
              <ShareBar row={row} />
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatHa(row.fact_area_ha)}
            </TableCell>
            <TableCell
              className={cn(
                'text-right tabular-nums',
                (row.delta_ha ?? 0) > 0 && 'text-primary',
                (row.delta_ha ?? 0) < 0 && 'text-accent-brand',
              )}
            >
              {formatDelta(row.delta_ha)}
            </TableCell>
            {economics && (
              <>
                <TableCell className="text-right tabular-nums">
                  {formatYield(row.yield_c_ha)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {formatTons(row.gross_tons)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {techCardsLabel(row)}
                  {row.gap && <div className="text-xs">{row.gap}</div>}
                </TableCell>
              </>
            )}
          </TableRow>
        ))}
        <TableRow className="bg-muted/50 font-semibold">
          <TableCell>Итого пашня</TableCell>
          <TableCell className="text-right tabular-nums">
            {data.arable_fields_count}
          </TableCell>
          <TableCell className="text-right tabular-nums">
            {formatHa(data.arable_ha)}
          </TableCell>
          <TableCell>100 %</TableCell>
          <TableCell className="text-right">—</TableCell>
          <TableCell className="text-right">—</TableCell>
          {economics && (
            <>
              <TableCell className="text-right">—</TableCell>
              <TableCell className="text-right tabular-nums">
                {formatTons(data.gross_tons)}
              </TableCell>
              <TableCell />
            </>
          )}
        </TableRow>
      </TableBody>
    </Table>
  );
}
